// appraisalStore — what an uploaded appraisal worksheet IS, before any of its rows are written.
//
// Two defects, one cause: the upload remembered nothing about the document itself.
//
//   1. THE PDF WAS NEVER KEPT. Rows were written and the worksheet thrown away, so a figure
//      that becomes production-to-count on a federal claim had no original to check it against.
//   2. THE DEDUPE KEY WAS THE FILE NAME. `silage_appraisals` is keyed on
//      (farmer_id, crop_year, source_file, line_no), so the SAME worksheet saved as
//      "Corn (UH).pdf" and again as "Corn (UH) 2.pdf" is two sets of rows — and every reader
//      sums them. Appraised production doubles with nothing on screen to say so.
//
// The document's identity is its BYTES (SHA-256), not its name. Same bytes already on file for
// this farm → nothing is written. Different bytes that cover units this farm already has
// appraisals for → the person uploading decides replace or add; nothing is guessed.
//
// ⚠ WHY "DECIDE", NOT "SUPERSEDE AUTOMATICALLY". Superseding needs a stable identity for an
// appraisal LINE across two different documents, and the data does not give one. Measured on
// the live table 2026-10-18: LANG FARMS LLC's R&H sheet holds 4 lines over only 3 distinct
// (FSA farm, tract, field, unit) identities — two lines carry no field id at all (the R&H form
// leaves Field ID blank as a matter of course). Matching those across a re-issue would be a
// guess, and a wrong guess deletes real production. So the overlap is REPORTED at the unit,
// and a human chooses.
//
// Pure functions, no imports — tools/check-appraisal-store.js loads this file by source.

// The storage path. ⚠ THE FARMER ID IS SEGMENT 1, NOT SEGMENT 2, ON PURPOSE: the
// farmer-documents bucket lets a portal user read any object whose SECOND path segment is one
// of their farms (`doc_path_farmer_id`). Claim figures are admin-only, so an appraisal must not
// sit where that rule can find it.
export function appraisalStoragePath(farmerId, cropYear, sha256, fileName, when) {
  const d = when instanceof Date ? when : new Date();
  const ymd = d.toISOString().slice(0, 10).replace(/-/g, "");
  const safe = String(fileName || "appraisal.pdf").replace(/[^a-zA-Z0-9._-]/g, "_");
  return `${farmerId}/${cropYear}/appraisal/${ymd}_${String(sha256 || "").slice(0, 8)}_${safe}`;
}

// The same worksheet, by content, already on file for this farm (any crop year).
// Returns null when no stored row carries this hash — including when the column does not exist
// yet, which is "no evidence", never "duplicate".
export function sameContentOnFile(existingRows, sha256) {
  if (!sha256) return null;
  const hits = (existingRows || []).filter((r) => r && r.content_sha256 && r.content_sha256 === sha256);
  if (!hits.length) return null;
  const first = hits.slice().sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")))[0];
  return { uploadedAt: first.created_at || null, sourceFile: first.source_file || null, rows: hits.length };
}

// The unit an appraisal line sits on. A unit number is the AIP's own key; where a form does
// not print one, the FSA farm stands in. Neither → no key, and the line overlaps nothing.
export function appraisalUnitKey(row) {
  if (!row) return null;
  const u = String(row.unit_number == null ? "" : row.unit_number).trim();
  if (u) return "unit:" + (/^\d+$/.test(u) ? String(Number(u)) : u.toUpperCase());
  const f = String(row.fsn == null ? "" : row.fsn).trim();
  return f ? "fsn:" + f : null;
}

// Existing rows that would STILL BE COUNTED beside the new upload on the same unit.
// A row this write overwrites in place (same source_file + line_no) is not a conflict — that is
// the key doing its job. Everything else on a shared unit, in the same crop year, is.
export function appraisalOverlap(existingRows, incomingRows) {
  const inc = (incomingRows || []).filter(Boolean);
  const overwritten = new Set(inc.map((r) => `${r.farmer_id}|${r.crop_year}|${r.source_file}|${r.line_no}`));
  const units = new Set();
  for (const r of inc) {
    const k = appraisalUnitKey(r);
    if (k) units.add(`${r.crop_year}|${k}`);
  }
  const conflicts = (existingRows || []).filter((r) => {
    if (!r) return false;
    if (overwritten.has(`${r.farmer_id}|${r.crop_year}|${r.source_file}|${r.line_no}`)) return false;
    const k = appraisalUnitKey(r);
    return !!k && units.has(`${r.crop_year}|${k}`);
  });
  const byFile = {};
  for (const r of conflicts) {
    const f = r.source_file || "(no file name)";
    const b = (byFile[f] = byFile[f] || { sourceFile: f, rows: 0, bu: 0, acres: 0, uploadedAt: r.created_at || null });
    b.rows++;
    b.bu += Number(r.appraised_bu) || 0;
    b.acres += Number(r.appraised_acres) || 0;
    if (r.created_at && (!b.uploadedAt || String(r.created_at) < String(b.uploadedAt))) b.uploadedAt = r.created_at;
  }
  const sharedUnits = [...new Set(conflicts.map(appraisalUnitKey))].sort();
  return { conflicts, files: Object.values(byFile), units: sharedUnits };
}

// Stamp provenance onto every row. ALL rows get both keys, so a bulk POST stays uniform
// (PostgREST refuses mixed key sets).
export function withProvenance(rows, sha256, storagePath) {
  return (rows || []).map((r) => ({ ...r, content_sha256: sha256 || null, storage_path: storagePath || null }));
}

// sql/099 may not be applied yet. The write must then fall back to the old shape rather than fail.
export function isMissingProvenanceColumn(message) {
  const m = String(message || "");
  return /content_sha256|storage_path/.test(m) && /(PGRST204|42703|column|schema cache)/i.test(m);
}
export function withoutProvenance(rows) {
  return (rows || []).map((r) => {
    const o = { ...r };
    delete o.content_sha256;
    delete o.storage_path;
    return o;
  });
}
